import React from 'react';
import { Button, Flex, NodeIconPill, TruncatedText } from '@chatsdet/ui';
import { ArrowLeft, Copy } from '@phosphor-icons/react';
import { NodeConfigPanel, type NodeConfigPanelHandle } from '../../../components/NodeConfigPanel';
import type { WorkflowNode } from '../../../../types/workflow';
import type { ActionPageContext } from '../types';
import type { NodeValidationError } from '../validation/types';
import { WorkflowVariablesPanel } from './WorkflowVariablesPanel';
import { WorkflowSummaryCard } from './WorkflowSummaryCard';
import { nodeLabel } from '../utils/nodeLabel';
import type { WorkflowInputDefinition, WorkflowScenario } from '@chatsdet/types';

type ConfigureTabPanelProps = {
  isActive: boolean;
  selectedNode: WorkflowNode | null;
  inferredPageContext: ActionPageContext | null;
  inferredPropertyId: string | null;
  upstreamSeedWorkflowId?: string | null;
  workflowId: string | null;
  nodeConfigPanelRef: React.RefObject<NodeConfigPanelHandle | null>;
  onUpdateNode: (nodeId: string, data: Record<string, any>) => Promise<void> | void;
  onDismiss: () => void;
  onDeselectNode: () => void;
  onNodeSelect?: (nodeId: string) => void;
  validationErrors?: NodeValidationError[];
  onCopyNode?: () => void;
  readOnly?: boolean;
  workflowInputs?: WorkflowInputDefinition[];
  workflowFlowType?: string;
  onSaveInputs?: (inputs: WorkflowInputDefinition[]) => void;
  isSavingInputs?: boolean;
  workflowScenarios?: WorkflowScenario[];
  onSaveScenarios?: (scenarios: WorkflowScenario[]) => void;
  isSavingScenarios?: boolean;
  onOpenScenarioConfig?: () => void;
  nodeCount?: number;
  onSwitchToRunsTab?: () => void;
};

export const ConfigureTabPanel: React.FC<ConfigureTabPanelProps> = ({
  isActive,
  selectedNode,
  inferredPageContext,
  inferredPropertyId,
  upstreamSeedWorkflowId,
  workflowId,
  nodeConfigPanelRef,
  onUpdateNode,
  onDismiss,
  onDeselectNode,
  onNodeSelect,
  validationErrors,
  onCopyNode,
  readOnly,
  workflowInputs,
  workflowFlowType,
  onSaveInputs,
  isSavingInputs,
  workflowScenarios,
  onSaveScenarios,
  isSavingScenarios,
  onOpenScenarioConfig,
  nodeCount,
  onSwitchToRunsTab,
}) => {
  const label = selectedNode
    ? nodeLabel([selectedNode], selectedNode.id)
    : null;

  if (selectedNode) {
    return (
      <div className="flex flex-col h-full min-h-0">
        <Flex
          align="center"
          gap="sm"
          className="shrink-0 px-4 pt-5 pb-3 border-b border-border"
        >
          <button
            type="button"
            onClick={onDeselectNode}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm transition-colors"
          >
            <ArrowLeft size={14} />
            <span>Workflow</span>
          </button>
          <span className="text-muted-foreground text-sm">·</span>
          <NodeIconPill nodeType={selectedNode.type ?? 'action'} size={14} />
          <div className="flex-1 min-w-0">
            <TruncatedText
              text={label ?? ''}
              className="text-sm font-medium"
              placement="bottom"
            />
          </div>
          <div className="shrink-0">
            {onCopyNode && !readOnly && (
              <Button variant="ghost" size="sm" onPress={onCopyNode} aria-label="Copy node">
                <Copy size={14} weight="bold" />
              </Button>
            )}
          </div>
        </Flex>
        <NodeConfigPanel
          ref={nodeConfigPanelRef}
          isActive={isActive}
          selectedNode={selectedNode}
          inferredPageContext={inferredPageContext}
          inferredPropertyId={inferredPropertyId}
          upstreamSeedWorkflowId={upstreamSeedWorkflowId}
          workflowId={workflowId}
          workflowInputs={workflowInputs}
          workflowFlowType={workflowFlowType}
          onUpdateNode={readOnly ? () => {} : onUpdateNode}
          onDismiss={onDismiss}
          validationErrors={validationErrors}
          readOnly={readOnly}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="shrink-0">
        <WorkflowSummaryCard
          workflowId={workflowId}
          nodeCount={nodeCount ?? 0}
          onSwitchToRunsTab={onSwitchToRunsTab}
        />
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
        <WorkflowVariablesPanel
          onNodeSelect={onNodeSelect}
          workflowId={workflowId}
          workflowInputs={workflowInputs}
          onSaveInputs={readOnly ? undefined : onSaveInputs}
          isSavingInputs={isSavingInputs}
          workflowScenarios={workflowScenarios}
          onSaveScenarios={readOnly ? undefined : onSaveScenarios}
          isSavingScenarios={isSavingScenarios}
          onOpenScenarioConfig={readOnly ? undefined : onOpenScenarioConfig}
        />
      </div>
    </div>
  );
};
